import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { 
  CalendarDays, Plus, Clock, Users, Video, MapPin, 
  Paperclip, Loader2, Check, X, ChevronLeft, ChevronRight,
  Repeat, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarPage = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    attendee_type: 'individual',
    attendee_ids: [],
    group_roles: [],
    location: '',
    include_meet_link: true,
    custom_meet_link: '',
    attachments: [],
    recurrence_type: 'none',
    recurrence_interval: 1,
    recurrence_end_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/meetings?include_past=true`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setMeetings(data.meetings || []);
      }
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not {users: [...]}
        setUsers(Array.isArray(data) ? data : (data.users || []));
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [token]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not {documents: [...]}
        setDocuments(Array.isArray(data) ? data : (data.documents || []));
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
    fetchDocuments();
  }, [fetchMeetings, fetchUsers, fetchDocuments]);

  const handleCreateMeeting = async () => {
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        attendee_type: formData.attendee_type,
        attendee_ids: formData.attendee_type === 'individual' ? formData.attendee_ids : null,
        group_roles: formData.attendee_type === 'group' ? formData.group_roles : null,
        location: formData.location || null,
        include_meet_link: formData.include_meet_link,
        custom_meet_link: formData.custom_meet_link || null,
        attachments: formData.attachments.length > 0 ? formData.attachments : null,
        recurrence: formData.recurrence_type !== 'none' ? {
          type: formData.recurrence_type,
          interval: formData.recurrence_interval,
          end_date: formData.recurrence_end_date || null
        } : null
      };

      const fetchOpts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify(payload)
      };

      let res;
      try {
        res = await fetch(`${API_URL}/api/meetings`, fetchOpts);
      } catch {
        // Retry once on network failure
        await new Promise(r => setTimeout(r, 1000));
        res = await fetch(`${API_URL}/api/meetings`, fetchOpts);
      }

      if (res.ok) {
        const data = await res.json();
        toast.success(`Meeting created! ${data.attendees_count} attendees notified.`);
        setCreateDialogOpen(false);
        resetForm();
        fetchMeetings();
      } else {
        let errMsg = `Failed to create meeting (${res.status})`;
        try { const error = await res.json(); errMsg = error.detail || errMsg; } catch {}
        if (res.status === 401 || res.status === 403) {
          errMsg = 'Session expired. Please log out and log back in.';
        }
        toast.error(errMsg);
      }
    } catch (err) {
      console.error('Create meeting error:', err);
      toast.error('Failed to create meeting: ' + (err.message || 'Network error. Please check your connection.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespondToMeeting = async (meetingId, response) => {
    try {
      const res = await fetch(`${API_URL}/api/meetings/${meetingId}/respond?response=${response}`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success(`Response recorded: ${response}`);
        fetchMeetings();
        setSelectedMeeting(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to respond');
      }
    } catch (err) {
      toast.error('Failed to respond to meeting');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) return;

    try {
      const res = await fetch(`${API_URL}/api/meetings/${meetingId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success('Meeting cancelled');
        fetchMeetings();
        setSelectedMeeting(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to cancel meeting');
      }
    } catch (err) {
      toast.error('Failed to cancel meeting');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      attendee_type: 'individual',
      attendee_ids: [],
      group_roles: [],
      location: '',
      include_meet_link: true,
      attachments: [],
      recurrence_type: 'none',
      recurrence_interval: 1,
      recurrence_end_date: ''
    });
  };

  const getMeetingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return meetings.filter(m => m.start_time.startsWith(dateStr));
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Get meetings for the selected week
  const getWeekMeetings = () => {
    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return meetings.filter(m => {
      const meetingDate = new Date(m.start_time);
      return meetingDate >= startOfWeek && meetingDate <= endOfWeek;
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('calendar.title')}
            </h1>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="create-meeting-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('calendar.scheduleMeeting')}
          </Button>
        </div>

        {/* Calendar View */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Widget */}
          <div className="bg-black/40 border border-white/10 rounded-sm p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-sm border-0"
              modifiers={{
                hasMeeting: meetings.map(m => new Date(m.start_time))
              }}
              modifiersStyles={{
                hasMeeting: { 
                  backgroundColor: 'rgba(168, 255, 96, 0.2)',
                  borderRadius: '4px'
                }
              }}
            />
          </div>

          {/* Meetings List */}
          <div className="lg:col-span-2 bg-black/40 border border-white/10 rounded-sm p-4">
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {formatDate(selectedDate.toISOString())}
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            ) : getMeetingsForDate(selectedDate).length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">{t('calendar.noMeetings')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {getMeetingsForDate(selectedDate).map((meeting) => (
                  <div
                    key={meeting.meeting_id}
                    onClick={() => setSelectedMeeting(meeting)}
                    className="p-4 bg-white/5 rounded-sm border border-white/10 hover:bg-white/10 cursor-pointer transition-colors"
                    data-testid={`meeting-${meeting.meeting_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-medium">{meeting.title}</h3>
                        <div className="flex items-center gap-4 mt-2 text-white/60 text-sm">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {meeting.attendees?.length || 0} {t('calendar.attendees').toLowerCase()}
                          </span>
                        </div>
                      </div>
                      {meeting.meet_link && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = meeting.meet_link.startsWith('http') ? meeting.meet_link : `https://${meeting.meet_link}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          className="flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary rounded-sm text-sm hover:bg-primary/30"
                          data-testid={`join-meeting-${meeting.meeting_id}`}
                        >
                          <Video className="w-4 h-4" />
                          {t('calendar.joinMeeting')}
                        </button>
                      )}
                    </div>
                    {meeting.my_status && !meeting.is_organizer && (
                      <div className="mt-2">
                        <span className={`text-xs px-2 py-1 rounded-sm ${
                          meeting.my_status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                          meeting.my_status === 'declined' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {meeting.my_status.charAt(0).toUpperCase() + meeting.my_status.slice(1)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Meetings */}
        <div className="bg-black/40 border border-white/10 rounded-sm p-4">
          <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Upcoming Meetings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meetings.slice(0, 6).map((meeting) => (
              <div
                key={meeting.meeting_id}
                onClick={() => setSelectedMeeting(meeting)}
                className="p-4 bg-white/5 rounded-sm border border-white/10 hover:bg-white/10 cursor-pointer"
              >
                <h3 className="text-white font-medium truncate">{meeting.title}</h3>
                <p className="text-white/60 text-sm mt-1">{formatDate(meeting.start_time)}</p>
                <p className="text-white/40 text-sm">{formatTime(meeting.start_time)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Create Meeting Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <CalendarDays className="w-5 h-5 text-primary" />
                {t('calendar.scheduleMeeting')}
              </DialogTitle>
              <DialogDescription className="text-white/60">
                {t('calendar.description')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Title */}
              <div>
                <Label className="text-white/80">{t('calendar.meetingTitle')} *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={t('calendar.meetingTitlePlaceholder')}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  data-testid="meeting-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-white/80">{t('calendar.description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('calendar.descriptionPlaceholder')}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  rows={2}
                />
              </div>

              {/* Date/Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">{t('calendar.startTime')} *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                    data-testid="meeting-start-input"
                  />
                </div>
                <div>
                  <Label className="text-white/80">{t('calendar.endTime')} *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                    data-testid="meeting-end-input"
                  />
                </div>
              </div>

              {/* Attendees */}
              <div>
                <Label className="text-white/80">{t('calendar.inviteType')}</Label>
                <Select
                  value={formData.attendee_type}
                  onValueChange={(v) => setFormData({...formData, attendee_type: v, attendee_ids: [], group_roles: []})}
                >
                  <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="individual" className="text-white">{t('calendar.specificPeople')}</SelectItem>
                    <SelectItem value="group" className="text-white">{t('calendar.byRole')}</SelectItem>
                    <SelectItem value="all" className="text-white">{t('calendar.allUsers')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.attendee_type === 'individual' && (
                <div>
                  <Label className="text-white/80">{t('calendar.selectPeople')}</Label>
                  <div className="mt-1 max-h-32 overflow-y-auto space-y-2 p-2 bg-black/50 rounded-sm border border-white/10">
                    {users.filter(u => u.user_id !== user?.user_id).map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.attendee_ids.includes(u.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, attendee_ids: [...formData.attendee_ids, u.user_id]});
                            } else {
                              setFormData({...formData, attendee_ids: formData.attendee_ids.filter(id => id !== u.user_id)});
                            }
                          }}
                          className="rounded border-white/20"
                        />
                        {u.name} ({u.email})
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.attendee_type === 'group' && (
                <div>
                  <Label className="text-white/80">{t('calendar.selectRoles')}</Label>
                  <div className="mt-1 flex gap-2 flex-wrap">
                    {['super_admin', 'admin', 'manager', 'user'].map((role) => (
                      <label key={role} className="flex items-center gap-2 text-white/80 text-sm cursor-pointer px-3 py-1 bg-black/50 rounded-sm border border-white/10">
                        <input
                          type="checkbox"
                          checked={formData.group_roles.includes(role)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, group_roles: [...formData.group_roles, role]});
                            } else {
                              setFormData({...formData, group_roles: formData.group_roles.filter(r => r !== role)});
                            }
                          }}
                          className="rounded border-white/20"
                        />
                        {role.replace('_', ' ').toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              <div>
                <Label className="text-white/80">{t('calendar.location')}</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder={t('calendar.locationPlaceholder')}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                />
              </div>

              {/* Meet Link */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-sm">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-primary" />
                  <span className="text-white/80 text-sm">{t('calendar.includeVideoCall')}</span>
                </div>
                <Switch
                  checked={formData.include_meet_link}
                  onCheckedChange={(v) => setFormData({...formData, include_meet_link: v, custom_meet_link: ''})}
                />
              </div>
              {formData.include_meet_link && (
                <div>
                  <Label className="text-white/80 text-sm">Meeting Link (paste Zoom, Google Meet, Teams URL — or leave blank to auto-generate)</Label>
                  <Input
                    value={formData.custom_meet_link}
                    onChange={(e) => setFormData({...formData, custom_meet_link: e.target.value})}
                    placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                    className="mt-1 bg-black/50 border-white/10 text-white placeholder:text-white/20"
                    data-testid="custom-meet-link-input"
                  />
                </div>
              )}

              {/* Recurrence */}
              <div>
                <Label className="text-white/80 flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  {t('calendar.recurrence')}
                </Label>
                <Select
                  value={formData.recurrence_type}
                  onValueChange={(v) => setFormData({...formData, recurrence_type: v})}
                >
                  <SelectTrigger className="mt-1 bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="none" className="text-white">{t('calendar.doesNotRepeat')}</SelectItem>
                    <SelectItem value="daily" className="text-white">{t('calendar.daily')}</SelectItem>
                    <SelectItem value="weekly" className="text-white">{t('calendar.weekly')}</SelectItem>
                    <SelectItem value="monthly" className="text-white">{t('calendar.monthly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.recurrence_type !== 'none' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">{t('calendar.every')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.recurrence_interval}
                      onChange={(e) => setFormData({...formData, recurrence_interval: parseInt(e.target.value)})}
                      className="mt-1 bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">{t('calendar.until')}</Label>
                    <Input
                      type="date"
                      value={formData.recurrence_end_date}
                      onChange={(e) => setFormData({...formData, recurrence_end_date: e.target.value})}
                      className="mt-1 bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Attachments */}
              {documents.length > 0 && (
                <div>
                  <Label className="text-white/80 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Attach Documents
                  </Label>
                  <div className="mt-1 max-h-24 overflow-y-auto space-y-1 p-2 bg-black/50 rounded-sm border border-white/10">
                    {documents.map((doc) => (
                      <label key={doc.document_id} className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.attachments.includes(doc.document_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, attachments: [...formData.attachments, doc.document_id]});
                            } else {
                              setFormData({...formData, attachments: formData.attachments.filter(id => id !== doc.document_id)});
                            }
                          }}
                          className="rounded border-white/20"
                        />
                        {doc.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {setCreateDialogOpen(false); resetForm();}}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateMeeting}
                  disabled={submitting || !formData.title || !formData.start_time || !formData.end_time}
                  className="bg-primary text-black hover:bg-primary/90"
                  data-testid="submit-meeting-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Meeting
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Meeting Details Dialog */}
        <Dialog open={!!selectedMeeting} onOpenChange={() => setSelectedMeeting(null)}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg">
            {selectedMeeting && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {selectedMeeting.title}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {/* Time */}
                  <div className="flex items-center gap-3 text-white/80">
                    <Clock className="w-5 h-5 text-primary" />
                    <div>
                      <p>{formatDate(selectedMeeting.start_time)}</p>
                      <p className="text-white/60 text-sm">
                        {formatTime(selectedMeeting.start_time)} - {formatTime(selectedMeeting.end_time)}
                      </p>
                    </div>
                  </div>

                  {/* Meet Link */}
                  {selectedMeeting.meet_link && (
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-primary" />
                      <button
                        onClick={() => {
                          const url = selectedMeeting.meet_link.startsWith('http') ? selectedMeeting.meet_link : `https://${selectedMeeting.meet_link}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                        className="text-primary hover:underline text-left"
                        data-testid="join-meeting-detail-btn"
                      >
                        {t('calendar.joinMeeting')}
                      </button>
                    </div>
                  )}

                  {/* Location */}
                  {selectedMeeting.location && (
                    <div className="flex items-center gap-3 text-white/80">
                      <MapPin className="w-5 h-5 text-primary" />
                      <span>{selectedMeeting.location}</span>
                    </div>
                  )}

                  {/* Organizer */}
                  <div className="flex items-center gap-3 text-white/80">
                    <Users className="w-5 h-5 text-primary" />
                    <span>{t('calendar.organizedBy')} {selectedMeeting.organizer_name}</span>
                  </div>

                  {/* Description */}
                  {selectedMeeting.description && (
                    <div className="p-3 bg-white/5 rounded-sm">
                      <p className="text-white/80 text-sm">{selectedMeeting.description}</p>
                    </div>
                  )}

                  {/* Attendees */}
                  <div>
                    <p className="text-white/60 text-sm mb-2">{t('calendar.attendees')} ({selectedMeeting.attendees?.length || 0})</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedMeeting.attendees?.slice(0, 5).map((a, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-1 rounded-sm ${
                            a.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                            a.status === 'declined' ? 'bg-red-500/20 text-red-400' :
                            'bg-white/10 text-white/60'
                          }`}
                        >
                          {a.name}
                        </span>
                      ))}
                      {selectedMeeting.attendees?.length > 5 && (
                        <span className="text-xs px-2 py-1 bg-white/10 text-white/60 rounded-sm">
                          +{selectedMeeting.attendees.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Attachments */}
                  {selectedMeeting.attachments?.length > 0 && (
                    <div>
                      <p className="text-white/60 text-sm mb-2 flex items-center gap-2">
                        <Paperclip className="w-4 h-4" />
                        Attachments
                      </p>
                      <div className="space-y-1">
                        {selectedMeeting.attachments.map((att, i) => (
                          <div key={i} className="text-white/80 text-sm px-2 py-1 bg-white/5 rounded-sm">
                            {att.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-white/10">
                    {!selectedMeeting.is_organizer && selectedMeeting.my_status === 'pending' && (
                      <>
                        <Button
                          onClick={() => handleRespondToMeeting(selectedMeeting.meeting_id, 'accepted')}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Accept
                        </Button>
                        <Button
                          onClick={() => handleRespondToMeeting(selectedMeeting.meeting_id, 'declined')}
                          variant="outline"
                          className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Decline
                        </Button>
                      </>
                    )}
                    {selectedMeeting.is_organizer && (
                      <Button
                        onClick={() => handleDeleteMeeting(selectedMeeting.meeting_id)}
                        variant="outline"
                        className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        {t('calendar.deleteMeeting')}
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CalendarPage;
